import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/Button.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { useToast } from "../hooks/useToast.js";

export function EditUserModal({ isOpen, setIsOpen, user, onUserUpdated }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact_number: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        contact_number: user.contact_number || "",
      });
      setErrors({});
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    const name = formData.name.trim();
    const email = formData.email.trim();
    const contact = formData.contact_number.trim();

    if (!name) newErrors.name = "Name is required.";
    if (!email) newErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Email must include '@' and '.' in a valid format.";

    if (!contact) newErrors.contact_number = "Contact number is required.";
    else if (!/^[0-9+\-\s]{10,}$/.test(contact))
      newErrors.contact_number = "Invalid phone number format.";

    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = Object.values(validationErrors).join(", ");
      toast({
        title: "Error",
        description: errorMessages,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      if (onUserUpdated) await onUserUpdated(formData);
      setIsOpen(false);
      toast({
        title: "Success",
        description: "User details have been updated.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog
          static
          open={isOpen}
          onClose={() => setIsOpen(false)}
          className="fixed inset-0 z-50 flex flex-col justify-end"
        >
          {/* Overlay Fade */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setIsOpen(false)}
          />

          {/* Bottom Sheet Modal */}
          <motion.div
            key="user-edit-modal"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 22,
              mass: 0.5,
            }}
            className="relative w-full bg-white rounded-t-3xl shadow-lg p-5 z-50"
          >
            {/* Handle Bar */}
            <div
              className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1 w-16 rounded-full"
              onClick={() => setIsOpen(false)}
            ></div>

            <Dialog.Title className="text-base font-semibold text-gray-800 mb-4 text-center">
              Edit User Details
            </Dialog.Title>

            {/* Form Content */}
            <div className="space-y-3 mb-4">
              {/* Name */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Name
                </Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="User name"
                  className="h-8 text-xs border-blue-100/50"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Email
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address"
                  className="h-8 text-xs border-blue-100/50"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Contact Number */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Contact Number
                </Label>
                <Input
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleChange}
                  maxLength={10}
                  pattern="\d{10}"
                  placeholder="Phone number"
                  className="h-8 text-xs border-blue-100/50"
                />
                {errors.contact_number && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact_number}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                className="h-8 px-3 text-xs rounded-full border border-gray-200 text-gray-600"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white border "
              >
                {loading ? "Updating..." : "Update"}
              </Button>
            </div>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
